import { centroid } from '@turf/turf';

import { GeoJSON } from 'ol/format';
import { Point } from 'ol/geom.js';
import OpenLayersStyle from 'ol/style/Style.js';
import { Fill, Icon, RegularShape, Stroke, Style } from 'ol/style.js';

import { Carto } from './carte-config.js';
import { toAlpha, toRgba } from './carte-style.js';

const DEFAULT_ICONE_RAYON = 32;

const POINT_RADIUS_MAX = 7;
const POINT_STROKE_WIDTH_MAX = 3;

export function initStyles() {
  if (!Carto.styles) {
    return;
  }
  Carto.styles.forEach(buildOpenLayersStyle);
}

function buildOpenLayersStyle(style) {
  style.olStyle = (feature) => {
    for (let regle of style.regles) {
      if (checkStyleCondition(regle, feature)) {
        return buildStyle(regle, feature);
      }
    }
  };
  style.regles.forEach((regle) => {
    regle.olStyleForLegende = (feature) => {
      return buildStyle(
        {
          ...regle,
          stylePoint: {
            ...regle.stylePoint,
            rayon:
              regle.stylePoint.rayon > POINT_RADIUS_MAX
                ? POINT_RADIUS_MAX
                : regle.stylePoint.rayon,
            epaisseurTrait:
              regle.stylePoint.epaisseurTrait > POINT_STROKE_WIDTH_MAX
                ? POINT_STROKE_WIDTH_MAX
                : regle.stylePoint.epaisseurTrait,
          },
        },
        feature,
      );
    };
    regle.countPositiveElements = (features) => {
      let count = 0;
      for (const feature of features) {
        // On check toutes les règles afin de savoir si une règle précédente match avant celle courante
        for (const otherRegle of style.regles) {
          if (checkStyleCondition(otherRegle, feature)) {
            if (regle === otherRegle) {
              count++;
            }
            break;
          }
        }
      }
      return count;
    };
  });
}

const prepareString = (value) => {
  if (!value) {
    return '';
  }
  // On enlève les accents et on met en minuscule
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace('œ', 'oe');
};

function checkStyleCondition(regle, feature) {
  if ((regle.conditions ?? []).length === 0) {
    return true;
  }

  const res = regle.conditions.map((condition) => {
    if (!condition.attribut || !condition.operateur) {
      // On ignore cette condition dans ce cas là
      return true;
    }

    const value = (feature.getProperties() ?? {})[condition.attribut];
    switch (condition.operateur) {
      case 'Different':
        if (typeof value === 'string') {
          return prepareString(value) !== prepareString(condition.valeur);
        }
        if (typeof value === 'number') {
          return Number(value ?? 0) !== Number(condition.valeur ?? 0);
        }
        return value !== condition.valeur;
      case 'Egal':
        if (typeof value === 'string') {
          return prepareString(value) === prepareString(condition.valeur);
        }
        if (typeof value === 'number') {
          return Number(value ?? 0) === Number(condition.valeur ?? 0);
        }
        return value === condition.valeur;
      case 'Greater':
        return Number(value ?? 0) > Number(condition.valeur ?? 0);
      case 'GreaterOrEqual':
        return Number(value ?? 0) >= Number(condition.valeur ?? 0);
      case 'Lower':
        return Number(value ?? 0) < Number(condition.valeur ?? 0);
      case 'LowerOrEqual':
        return Number(value ?? 0) <= Number(condition.valeur ?? 0);
      case 'Contains':
        return prepareString(value).includes(prepareString(condition.valeur));
      case 'Like':
        if (!condition.valeur) {
          return true;
        }
        // On ne clean pas "condition.valeur" ni "value" avec prepareString
        // => on laisse la regexp s'occuper de gérer le coup
        return new RegExp(condition.valeur, 'i').test(value ?? '');
      case 'StartsWith':
        return prepareString(value).startsWith(prepareString(condition.valeur));
      case 'EndsWith':
        return prepareString(value).endsWith(prepareString(condition.valeur));
      case 'Empty':
        return value === null || value === undefined || value === '';
      case 'NotEmpty':
        return value !== null && value !== undefined && value !== '';
    }
  });
  // AtLeastOne / All -> Au moins 1 vrai / tous vrai (non tous faux)
  return regle.regleOperateur === 'AtLeastOne'
    ? res.some((v) => v === true)
    : res.every((v) => v === true);
}

const isImageLoaded = (src) => {
  const image = new Image();
  image.src = src;

  return image.complete || image.width > 0 || image.height > 0;
};

const buildStyle = (regle, feature) => {
  if (regle.barycentre) {
    const geometry = feature.getGeometry();
    if (!geometry) {
      return [];
    }

    return getPointStyles(regle.stylePoint).map(
      (style) =>
        new Style({
          geometry: buildBarycentrePoint(feature),
          image: style.getImage(),
        }),
    );
  }

  if (regle.miniature) {
    const miniature = feature.get('miniature') || feature.get('urlImg');
    if (!miniature || miniature.endsWith('non-consultable')) {
      return;
    }

    // Si la miniature n'est pas encore mise en cache, alors on indique la propriété "src"
    // Sinon on indique la propriété "img"
    // Juste utiliser "src" provoque un clignottement intempestif si le nombre de miniature est "conséquent"
    // Juste utiliser "img" provoque une carte sans miniature dans le cas où les miniatures n'étaient pas mises en cache
    // On jongle donc entre les deux propriétés selon que l'image est dans le cache ou non...
    const options = {};
    if (isImageLoaded(miniature)) {
      const image = new Image();
      image.src = miniature;
      options.img = image;
    } else {
      options.src = miniature;
    }

    return new Style({
      geometry: buildBarycentrePoint,
      image: new Icon({
        anchorXUnits: 'fraction',
        anchorYUnits: 'fraction',
        anchor: [0.5, 0.5],
        ...options,
      }),
    });
  }

  if (regle.iconeId) {
    return new Style({
      geometry: buildBarycentrePoint(feature),
      image: new Icon({
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        src: `/pictos/index/${regle.iconeId}`,
        height: regle?.iconeRayon ?? DEFAULT_ICONE_RAYON,
        width: regle?.iconeRayon ?? DEFAULT_ICONE_RAYON,
      }),
    });
  }

  return regleStylesToOpenlayersStyle(
    regle.stylePoint,
    regle.styleLigne,
    regle.stylePolygone,
    feature,
  );
};

const regleStylesToOpenlayersStyle = (
  regleStylePoint,
  regleStyleLigne,
  regleStylePolygone,
  feature,
) => {
  const polygonStyles = getPolygoneStyles(regleStylePolygone);
  const multiPolygonStyles = polygonStyles;

  const lineStringStyles = getLigneStyles(regleStyleLigne);
  const multiLineStringStyles = lineStringStyles;

  const circleStyles = polygonStyles.concat(lineStringStyles);

  const pointStyles = getPointStyles(regleStylePoint);

  const multiPointStyles = pointStyles;
  const geometryCollectionStyles = polygonStyles.concat(
    lineStringStyles,
    pointStyles,
  );

  const styles = {
    Point: pointStyles,
    LineString: lineStringStyles,
    Polygon: polygonStyles,
    MultiPoint: multiPointStyles,
    MultiLineString: multiLineStringStyles,
    MultiPolygon: multiPolygonStyles,
    GeometryCollection: geometryCollectionStyles,
    Circle: circleStyles,
    // Toujours inclu dans un Polygone et n'est pas rendu seul,
    // il n'est donc pas concerné par des styles
    LinearRing: [],
  };

  const geometry = feature.getGeometry();
  if (!geometry) {
    return [];
  }

  const type = geometry.getType();
  return styles[type];
};

const buildBarycentrePoint = (feature) => {
  const geometry = feature.getGeometry();
  if (!geometry) {
    return undefined;
  }

  switch (geometry.getType()) {
    case 'Point':
      return geometry;
    case 'GeometryCollection':
    // fall through
    case 'LineString':
    // fall through
    case 'LinearRing':
    // fall through
    case 'MultiPoint':
    // fall through
    case 'MultiLineString':
    // fall through
    case 'MultiPolygon':
    // fall through
    case 'Circle':
    // fall through
    default: {
      const geojson = new GeoJSON().writeFeaturesObject([feature]);
      const turfFeature = centroid(geojson);
      return new Point(turfFeature.geometry.coordinates);
    }
  }
};

const getNumberOfVertices = (formeGeometrique) => {
  switch (formeGeometrique) {
    case 'POINT':
      return Infinity;
    case 'CARRE':
      return 4;
    case 'TRIANGLE':
      return 3;
    case 'PENTAGONE':
      return 5;
    case 'HEXAGONE':
      return 6;
  }
  return 0;
};

const getPointStyles = (regleStylePoint) => {
  const fill = new Fill({
    color: toRgba(
      regleStylePoint?.couleurFond ?? '#000000',
      toAlpha(regleStylePoint?.opaciteFond ?? 100),
    ),
  });
  const stroke = new Stroke({
    color: toRgba(
      regleStylePoint?.couleurTrait ?? '#000000',
      toAlpha(regleStylePoint?.opaciteTrait ?? 0),
    ),
    width: regleStylePoint?.epaisseurTrait ?? 0,
    lineDash: getLineStyleDataWithSize(
      regleStylePoint?.motifTrait ?? 'Solid',
      regleStylePoint?.epaisseurTrait ?? 0,
    ),
    lineJoin: 'miter',
  });
  const radius = regleStylePoint?.rayon ?? 0;

  const formeGeometrique = regleStylePoint?.formeGeometrique ?? 'Point';
  return [
    new OpenLayersStyle({
      image: new RegularShape({
        fill,
        stroke,
        points: getNumberOfVertices(formeGeometrique),
        radius,
        angle: Math.PI / 4,
      }),
      zIndex: Infinity,
    }),
  ];
};

const getLigneStyles = (regleStyleLigne) => [
  // OpenLayers ne supporte pas les contours pour les lignes.
  // On met donc une deuxième ligne en dessous, plus grande que la ligne principale, pour ressembler à un contour.
  // Cependant, cela ne joue pas bien avec les styles de contour.
  new OpenLayersStyle({
    stroke: new Stroke({
      width:
        (regleStyleLigne?.epaisseurContour ?? 1) +
        (regleStyleLigne?.epaisseurTrait ?? 0),
      // Un trait apparaît même avec une taille de 0, donc on change l'opacité à la place pour y faire disparaître
      color: toRgba(
        regleStyleLigne?.couleurContour ?? '#000000',
        (regleStyleLigne?.epaisseurContour ?? 1) !== 0
          ? toAlpha(regleStyleLigne?.opaciteContour ?? 0)
          : 0,
      ),
      lineDash: getLineStyleDataWithSize(
        regleStyleLigne?.motifContour ?? 'Solid',
        (regleStyleLigne?.epaisseurContour ?? 1) +
          (regleStyleLigne?.epaisseurTrait ?? 0),
      ),
    }),
  }),
  new OpenLayersStyle({
    stroke: new Stroke({
      width: regleStyleLigne?.epaisseurTrait ?? 0,
      color: toRgba(
        regleStyleLigne?.couleurTrait ?? '#000000',
        toAlpha(regleStyleLigne?.opaciteTrait ?? 100),
      ),
      lineDash: getLineStyleDataWithSize(
        regleStyleLigne?.motifTrait ?? 'Solid',
        regleStyleLigne?.epaisseurTrait ?? 0,
      ),
    }),
  }),
];

const getPolygoneStyles = (regleStylePolygone) => {
  const fillColorHexa = toRgba(
    regleStylePolygone?.couleurFond ?? '#000000',
    toAlpha(regleStylePolygone?.opaciteFond ?? 100),
  );

  const motifFond = regleStylePolygone?.motifFond ?? 'UNI';
  if (motifFond === 'UNI') {
    return [
      new OpenLayersStyle({
        fill: new Fill({
          color: fillColorHexa,
        }),
        stroke: new Stroke({
          width: regleStylePolygone?.epaisseurTrait ?? 0,
          color: toRgba(
            regleStylePolygone?.couleurTrait ?? '#000000',
            toAlpha(regleStylePolygone?.opaciteTrait ?? 0),
          ),
          lineDash: getLineStyleDataWithSize(
            regleStylePolygone?.motifTrait ?? 'Solid',
            regleStylePolygone?.epaisseurTrait ?? 0,
          ),
        }),
      }),
    ];
  }

  let pattern;
  if (motifFond === 'POINTS') {
    pattern = buildDotPattern(3, fillColorHexa);
  } else if (motifFond === 'HACHURES') {
    pattern = buildCrossHatchedPattern(4, fillColorHexa);
  }
  return [
    new OpenLayersStyle({
      fill: new Fill({
        color: pattern,
      }),
      stroke: new Stroke({
        width: regleStylePolygone?.epaisseurTrait ?? 0,
        color: toRgba(
          regleStylePolygone?.couleurTrait ?? '#000000',
          toAlpha(regleStylePolygone?.opaciteTrait ?? 0),
        ),
        lineDash: getLineStyleDataWithSize(
          regleStylePolygone?.motifTrait ?? 'Solid',
          regleStylePolygone?.epaisseurTrait ?? 0,
        ),
      }),
    }),
  ];
};

const getLineStyleDataWithSize = (linePattern, size = 1) => {
  if (!linePattern) return [];

  switch (linePattern) {
    case 'Dash':
      return [4 + size, 4 + size];
    case 'Dashdot':
      return [6 + size, 2 + size, 2 + size, 2 + size];
    case 'Dot':
      return [2 + size, 2 + size];
    case 'Longdash':
      return [8 + size, 8 + size];
    case 'Longdashdot':
      return [8 + size, 2 + size, 2 + size, 2 + size];
    default:
      return [];
  }
};

const buildDotPattern = (radius, backgroundColor) => {
  const canvas = document.createElement('canvas');
  canvas.width = radius * 3;
  canvas.height = radius * 3;
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, radius * 3, radius * 3);
  context.fill();
  context.beginPath();
  context.fillStyle = getDotColor(backgroundColor);
  context.arc(radius * 1.5, radius * 1.5, radius / 2, 0, 2 * Math.PI, false);
  context.fill();

  return context.createPattern(canvas, 'repeat');
};

const buildCrossHatchedPattern = (radius, backgroundColor) => {
  const canvas = document.createElement('canvas');
  canvas.width = radius * 3;
  canvas.height = radius * 3;
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, radius * 3, radius * 3);
  context.fill();
  context.beginPath();
  context.fillStyle = getDotColor(backgroundColor);
  context.fillRect(radius * 1.5 - radius / 4, 0, radius / 2, radius * 3);
  context.fillRect(0, 0, radius / 4, radius * 3);
  context.fillRect(radius * 3 - radius / 4, 0, radius / 4, radius * 3);
  context.fill();
  context.setTransform(1, 0, 0, 1, 0, 0);

  return context.createPattern(canvas, 'repeat');
};

const getDotColor = (backgroundColor) => {
  let dotColor = backgroundColor;
  if (backgroundColor.indexOf('#') === 0) {
    dotColor = backgroundColor.slice(1);
  }
  const red = Number.parseInt(dotColor.slice(0, 2), 16);
  const green = Number.parseInt(dotColor.slice(2, 4), 16);
  const blue = Number.parseInt(dotColor.slice(4, 6), 16);
  const alpha = dotColor.slice(6, 8) || 'FF';

  // https://stackoverflow.com/a/3943023/112731
  return red * 0.299 + green * 0.587 + blue * 0.114 > 186
    ? `#000000${alpha}`
    : `#FFFFFF${alpha}`;
};
